/**
 * Infrastructure Cascade Analysis — models failure propagation through
 * the global infrastructure dependency graph.
 *
 * Algorithm:
 * - BFS traversal up to depth 3 from affected nodes
 * - Impact scoring: strength × disruption × (1 - redundancy)
 * - Minimum 0.05 impact threshold to prune insignificant paths
 * - Aggregates total impact across all affected downstream nodes
 */

import type { CascadeResult, CascadePathStep } from './types';
import {
  INFRA_NODES,
  getNodeById,
  getDownstream,
} from '@/config/infrastructure-graph';

const MAX_DEPTH = 3;
const MIN_IMPACT_THRESHOLD = 0.05;

// ---------------------------------------------------------------------------
// BFS cascade traversal
// ---------------------------------------------------------------------------

interface BFSEntry {
  nodeId: string;
  depth: number;
  cumulativeImpact: number;
  path: CascadePathStep[];
}

/**
 * Perform BFS cascade from a single source node.
 * Tracks cumulative impact as it propagates through edges.
 */
function cascadeFromNode(sourceNodeId: string): CascadeResult | null {
  const sourceNode = getNodeById(sourceNodeId);
  if (!sourceNode) return null;

  const visited = new Set<string>();
  visited.add(sourceNodeId);

  const queue: BFSEntry[] = [{
    nodeId: sourceNodeId,
    depth: 0,
    cumulativeImpact: 1.0, // Source has full impact
    path: [{
      nodeId: sourceNodeId,
      nodeName: sourceNode.name,
      depth: 0,
      impact: 1.0,
    }],
  }];

  const affectedNodes: { nodeId: string; nodeName: string; impact: number }[] = [];
  const allPaths: CascadePathStep[][] = [];
  let maxDepth = 0;

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (current.depth >= MAX_DEPTH) continue;

    const downstream = getDownstream(current.nodeId);

    for (const edge of downstream) {
      if (visited.has(edge.to)) continue;

      // Calculate impact through this edge
      const edgeImpact = edge.strength * edge.disruption * (1 - edge.redundancy);
      const propagatedImpact = current.cumulativeImpact * edgeImpact;

      // Prune insignificant paths
      if (propagatedImpact < MIN_IMPACT_THRESHOLD) continue;

      const targetNode = getNodeById(edge.to);
      if (!targetNode) continue;

      visited.add(edge.to);

      const newDepth = current.depth + 1;
      if (newDepth > maxDepth) maxDepth = newDepth;

      const step: CascadePathStep = {
        nodeId: edge.to,
        nodeName: targetNode.name,
        depth: newDepth,
        impact: Math.round(propagatedImpact * 1000) / 1000,
      };

      const newPath = [...current.path, step];

      affectedNodes.push({
        nodeId: edge.to,
        nodeName: targetNode.name,
        impact: step.impact,
      });

      allPaths.push(newPath);

      queue.push({
        nodeId: edge.to,
        depth: newDepth,
        cumulativeImpact: propagatedImpact,
        path: newPath,
      });
    }
  }

  if (affectedNodes.length === 0) return null;

  // Deduplicate affected nodes (keep highest impact for each)
  const nodeImpacts = new Map<string, { nodeId: string; nodeName: string; impact: number }>();
  for (const node of affectedNodes) {
    const existing = nodeImpacts.get(node.nodeId);
    if (!existing || existing.impact < node.impact) {
      nodeImpacts.set(node.nodeId, node);
    }
  }

  const dedupedNodes = Array.from(nodeImpacts.values());
  dedupedNodes.sort((a, b) => b.impact - a.impact);

  const totalImpact = dedupedNodes.reduce((sum, n) => sum + n.impact, 0);

  return {
    sourceNodeId,
    sourceNodeName: sourceNode.name,
    affectedNodes: dedupedNodes,
    totalImpact: Math.round(totalImpact * 1000) / 1000,
    maxDepth,
    paths: allPaths.slice(0, 20), // Cap paths to avoid huge results
  };
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Analyze infrastructure cascades for a set of affected node IDs.
 *
 * @param affectedNodeIds - IDs of infrastructure nodes that are disrupted
 *   (e.g., detected from events tagged with infrastructure keywords)
 * @returns Array of CascadeResult showing downstream propagation
 */
export function analyzeCascade(affectedNodeIds: string[]): CascadeResult[] {
  const results: CascadeResult[] = [];

  for (const nodeId of affectedNodeIds) {
    const result = cascadeFromNode(nodeId);
    if (result) {
      results.push(result);
    }
  }

  // Sort by total impact descending
  results.sort((a, b) => b.totalImpact - a.totalImpact);

  return results;
}

/**
 * Find infrastructure nodes that match event keywords.
 * Used to identify which infrastructure nodes are affected by current events.
 */
export function findAffectedNodes(
  events: { title: string; description?: string; tags?: string[] }[],
): string[] {
  const matched = new Set<string>();

  // Build a simple name-to-id lookup from infrastructure nodes
  const nodePatterns: { pattern: string; id: string }[] = [];
  for (const node of INFRA_NODES) {
    // Use significant words from the name (skip common words)
    const words = node.name.toLowerCase().split(/[\s/()-]+/);
    for (const word of words) {
      if (word.length >= 4) {
        nodePatterns.push({ pattern: word, id: node.id });
      }
    }
  }

  for (const event of events) {
    const text = `${event.title} ${event.description ?? ''} ${(event.tags ?? []).join(' ')}`.toLowerCase();

    for (const { pattern, id } of nodePatterns) {
      if (matched.has(id)) continue;
      if (text.includes(pattern)) {
        matched.add(id);
      }
    }
  }

  return Array.from(matched);
}
